// Pure helpers for the Cart Analytics page (no React / DOM), unit-tested in src/test.

const isBlank = (v) => v === null || v === undefined || v === '';

const toNumber = (v) => {
  if (isBlank(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const formatMoney = (value, ccy) => {
  const n = toNumber(value);
  if (n === null) return '-';
  const text = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return ccy ? `${ccy} ${text}` : text;
};

export const formatPct = (n) => {
  const v = toNumber(n);
  return v === null ? '-' : `${v.toFixed(1)}%`;
};

// Change of `cur` against `prev`: { text, dir } with dir in up | down | flat | new.
export const deltaInfo = (cur, prev) => {
  const c = toNumber(cur) || 0;
  const p = toNumber(prev) || 0;
  if (p === 0) {
    return c > 0 ? { text: 'New', dir: 'new' } : { text: '0.0%', dir: 'flat' };
  }
  const pct = ((c - p) / p) * 100;
  if (pct === 0) return { text: '0.0%', dir: 'flat' };
  const sign = pct > 0 ? '+' : '-';
  return { text: `${sign}${Math.abs(pct).toFixed(1)}%`, dir: pct > 0 ? 'up' : 'down' };
};

const isoDay = (d) => d.toISOString().slice(0, 10);

// Last 30 days ending today, as YYYY-MM-DD (UTC, to match the API's date strings).
export const defaultRange = (today = new Date()) => {
  const to = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - 30);
  return { from: isoDay(from), to: isoDay(to) };
};

// Mirrors the API's range caps (RequestParser): 92 days for day buckets, 366 otherwise.
export const maxRangeDays = (period) => (period === 'day' ? 92 : 366);

const SIGNUP_LABELS = {
  GOOGLE: 'Google',
  FACEBOOK: 'Facebook',
  EMAIL: 'Email',
  GUEST: 'Guest',
  'OLD-SITE': 'Old site',
  'FIRST-USER-LOGIN': 'First login',
};

export const signupLabel = (code) => {
  if (isBlank(code)) return '-';
  return SIGNUP_LABELS[code] || code;
};

const pad2 = (n) => String(n).padStart(2, '0');

// Local calendar day as YYYY-MM-DD (date pickers hand back local-midnight Dates, so
// toISOString() would shift the day for users east of UTC).
export const toApiDate = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

export const parseApiDate = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

// Client-side mirror of the API's range validation; returns a message or null.
export const rangeError = (from, to, period) => {
  const f = parseApiDate(from);
  const t = parseApiDate(to);
  if (!f || !t) return 'Please select both dates';
  if (f > t) return 'From date must be before To date';
  const days = Math.round((Date.UTC(t.getFullYear(), t.getMonth(), t.getDate()) - Date.UTC(f.getFullYear(), f.getMonth(), f.getDate())) / 86400000);
  const max = maxRangeDays(period);
  if (days > max) return `Range too long: maximum ${max} days for the ${period} view`;
  return null;
};

// ───────────────────────── infographic helpers ─────────────────────────

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Carts split into completed / incomplete / saved. The API classifies each cart once, so the
// three always add up to the total.
export const funnelStages = (current) => {
  const c = current || {};
  const completed = toNum(c.completed_carts);
  const incomplete = toNum(c.open_carts);
  const saved = toNum(c.save_later_carts);
  const total = toNum(c.carts) || completed + incomplete + saved;
  const pct = (v) => (total > 0 ? (v / total) * 100 : 0);
  return {
    total,
    stages: [
      { key: 'completed', label: 'Completed', value: completed, pct: pct(completed) },
      { key: 'incomplete', label: 'Incomplete', value: incomplete, pct: pct(incomplete) },
      { key: 'saved', label: 'Saved for later', value: saved, pct: pct(saved) },
    ],
  };
};

// Values of one trend metric, oldest first, for one currency ('' = all).
export const sparkSeries = (trend, key, ccy) => (trend || [])
  .filter((r) => !ccy || r.ccy === ccy)
  .slice()
  .sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)))
  .map((r) => toNum(r[key]));

// "x,y x,y ..." for an SVG polyline inside a w×h box (larger value = higher up).
export const sparkPoints = (values, w, h) => {
  if (!values || values.length === 0) return '';
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const step = values.length > 1 ? w / (values.length - 1) : 0;
  return values.map((v, i) => {
    const x = values.length > 1 ? i * step : w / 2;
    const y = span === 0 ? h / 2 : h - ((v - min) / span) * h;
    return `${Number(x.toFixed(2))},${Number(y.toFixed(2))}`;
  }).join(' ');
};

// Arc lengths for a donut drawn with stroke-dasharray on a circle whose circumference is `circumference`.
export const donutSegments = (parts, circumference) => {
  const items = (parts || []).map((p) => ({ ...p, value: Math.max(0, toNum(p.value)) }));
  const total = items.reduce((s, p) => s + p.value, 0);
  if (total <= 0) return [];
  let offset = 0;
  return items.map((p) => {
    const share = p.value / total;
    const seg = { ...p, pct: share * 100, length: share * circumference, offset };
    offset += seg.length;
    return seg;
  });
};

const sizeOf = (row) => [row.soi_width, row.soi_height, row.soi_depth]
  .map(toNum)
  .filter((n) => n > 0)
  .map((n) => String(Number(n.toFixed(2))))
  .join('×');

const hasValue = (v) => v !== undefined && v !== null && String(v).trim() !== '';

// Size and item id (from the info view) come from the Measurement step only: the size is stored on every
// step row of a line, so repeating it per option is noise. Without a Measurement step the first row with
// a size is used.
export const measurementInfo = (info) => {
  const rows = info || [];
  if (rows.length === 0) return { size: '', itemId: '', options: [] };
  const measure = rows.find((r) => /measure/i.test(`${r.ss_desc || ''} ${r.ss_code_name || ''} ${r.sps_desc || ''}`));
  const sizeRow = measure || rows.find((r) => toNum(r.soi_width) > 0 || toNum(r.soi_height) > 0);
  const idRow = [measure, ...rows].find((r) => r && hasValue(r.item_id));
  return {
    size: sizeRow ? sizeOf(sizeRow) : '',
    itemId: idRow ? String(idRow.item_id) : '',
    options: rows
      .filter((r) => r !== measure)
      .map((r) => ({ label: String(r.ss_desc || '').trim(), value: String(r.sps_desc || '').trim() }))
      .filter((o) => o.label || o.value),
  };
};

export const CART_TABS = [
  { key: 'INCOMPLETE', label: 'Incomplete' },
  { key: 'COMPLETED', label: 'Completed' },
  { key: 'SAVE_LATER', label: 'Saved for later' },
];

export const cartStatusLabel = (state) => (CART_TABS.find((t) => t.key === state) || { label: state }).label;

export const cartTabCounts = (carts) => CART_TABS.reduce((m, t) => {
  m[t.key] = (carts || []).filter((c) => c.state === t.key).length;
  return m;
}, {});

export const filterCartsByTab = (carts, tab) => (carts || []).filter((c) => c.state === tab);

export const defaultCartTab = (counts) => (CART_TABS.find((t) => (counts || {})[t.key] > 0) || CART_TABS[0]).key;

// First two addresses until the user asks for more.
export const visibleAddresses = (list, expanded, limit = 2) => {
  const all = list || [];
  if (expanded || all.length <= limit) return { visible: all, hidden: 0 };
  return { visible: all.slice(0, limit), hidden: all.length - limit };
};

// Area, city, state, country by description (the API falls back to codes only when no description exists).
export const addressPlace = (a) => {
  const x = a || {};
  const parts = [
    x.ar_desc || x.cad_city_area,
    x.ct_desc || x.cad_city,
    x.st_desc || x.cad_state,
    x.country_desc || x.cad_country,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : '-';
};
